import firebase from 'firebase/compat/app';
import { auth, db, rootRef } from './firebase';
import { roleOf } from '../utils/role';
import type { Member } from '../types';

const SECONDARY_NAME = 'Secondary';

/** 최상위 컬렉션 — 앱 데이터 경로와 분리해 규칙 매칭을 단순화 */
export const AUTH_LINKS_COLLECTION = 'pf_auth_links';

/** 로그인 바인딩 중 App이 익명 재로그인을 하지 않도록 억제 */
let suppressAnonymousRestore = false;

export function setSuppressAnonymousRestore(value: boolean) {
  suppressAnonymousRestore = value;
}

export function shouldSuppressAnonymousRestore() {
  return suppressAnonymousRestore;
}

/** Firebase Auth용 내부 이메일 (화면에 노출하지 않음) */
export function authEmailFor(memberId: string): string {
  const domain = (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string) || 'localhost';
  const local = `m${memberId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 60)}` || 'user';
  return `${local}@${domain}`;
}

function getSecondaryAuth() {
  const existing = firebase.apps.find((a) => a.name === SECONDARY_NAME);
  const app = existing || firebase.initializeApp(firebase.app().options, SECONDARY_NAME);
  return app.auth();
}

function errCode(err: unknown): string {
  return (err as { code?: string }).code || '';
}

function errMessage(err: unknown): string {
  return (err as { message?: string }).message || String(err);
}

function authLinkRef(uid: string) {
  return db.collection(AUTH_LINKS_COLLECTION).doc(uid);
}

/**
 * HR가 타 계정 생성 시: 현재 로그인 세션을 유지한 채 Auth 유저만 만든다.
 */
export async function createAuthUserKeepingSession(memberId: string, password: string): Promise<string> {
  const email = authEmailFor(memberId);
  const secondary = getSecondaryAuth();
  try {
    const cred = await secondary.createUserWithEmailAndPassword(email, password);
    const uid = cred.user!.uid;
    await secondary.signOut();
    return uid;
  } catch (err: unknown) {
    if (errCode(err) === 'auth/email-already-in-use') {
      const cred = await secondary.signInWithEmailAndPassword(email, password);
      const uid = cred.user!.uid;
      await secondary.signOut();
      return uid;
    }
    throw err;
  }
}

export async function writeAuthLink(member: Pick<Member, 'id' | 'duty' | 'isAdmin'>) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated');
  if (auth.currentUser!.isAnonymous) {
    throw new Error('Anonymous session cannot write auth_link');
  }

  const snap = await rootRef().collection('members').doc(member.id).get();
  if (!snap.exists) {
    throw new Error(`Member not found: ${member.id}`);
  }
  const data = snap.data() || {};
  const fresh = {
    id: member.id,
    name: data.name || member.id,
    duty: data.duty || member.duty,
    department: data.department || '',
    isAdmin: data.isAdmin === true,
  };

  await authLinkRef(uid).set({
    memberId: fresh.id,
    role: roleOf(fresh),
    duty: fresh.duty,
    updatedAt: Date.now(),
  });
}

export async function clearAuthLink() {
  const uid = auth.currentUser?.uid;
  if (!uid || auth.currentUser?.isAnonymous) return;
  try {
    await authLinkRef(uid).delete();
  } catch {
    /* ignore */
  }
}

async function ensureEmailAuthSession(email: string, password: string): Promise<void> {
  if (auth.currentUser && !auth.currentUser.isAnonymous && auth.currentUser.email === email) {
    return;
  }

  if (auth.currentUser) {
    await auth.signOut();
  }

  try {
    await auth.signInWithEmailAndPassword(email, password);
    return;
  } catch (signInErr: unknown) {
    const code = errCode(signInErr);
    const tryCreate =
      code === 'auth/user-not-found' ||
      code === 'auth/invalid-login-credentials' ||
      code === 'auth/invalid-credential' ||
      code === 'auth/wrong-password';

    if (!tryCreate) throw signInErr;

    try {
      await auth.createUserWithEmailAndPassword(email, password);
      return;
    } catch (createErr: unknown) {
      const createCode = errCode(createErr);
      if (createCode === 'auth/email-already-in-use') {
        await auth.signInWithEmailAndPassword(email, password);
        return;
      }
      throw createErr;
    }
  }
}

/**
 * 앱 비밀번호 검증 후 Firebase Email Auth 세션으로 전환하고
 * members.authUid + pf_auth_links 를 기록한다.
 */
export async function bindMemberSession(
  member: Member & { authUid?: string; authEmail?: string },
  password: string
): Promise<void> {
  const email = member.authEmail || authEmailFor(member.id);
  setSuppressAnonymousRestore(true);

  try {
    await ensureEmailAuthSession(email, password);

    const uid = auth.currentUser?.uid;
    if (!uid || auth.currentUser?.isAnonymous) {
      throw new Error('Failed to establish password auth session');
    }

    await auth.currentUser!.getIdToken(true);

    const memberRef = rootRef().collection('members').doc(member.id);
    const linkRef = authLinkRef(uid);

    let snap;
    try {
      snap = await memberRef.get();
    } catch (readErr: unknown) {
      console.error('[AUTH] members read failed:', readErr);
      const wrapped = readErr as { code?: string; step?: string };
      wrapped.step = 'members_read';
      throw readErr;
    }

    if (!snap.exists) {
      throw new Error(`Member not found: ${member.id}`);
    }

    const data = snap.data() || {};
    const duty = data.duty || member.duty || '팀원';
    const role = roleOf({
      duty,
      isAdmin: data.isAdmin === true,
    });

    if (data.authUid !== uid || data.authEmail !== email) {
      try {
        await memberRef.update({
          authUid: uid,
          authEmail: email,
        });
      } catch (updateErr: unknown) {
        console.error('[AUTH] members authUid update failed:', updateErr);
        const wrapped = updateErr as { code?: string; step?: string };
        wrapped.step = 'members_update';
        throw updateErr;
      }
    }

    try {
      console.info('[AUTH] writing pf_auth_links', { uid, memberId: member.id, role, duty });
      await linkRef.set({
        memberId: member.id,
        role,
        duty,
        updatedAt: Date.now(),
      });
    } catch (linkErr: unknown) {
      console.error('[AUTH] pf_auth_links write failed:', linkErr, {
        uid,
        email: auth.currentUser?.email,
        provider: auth.currentUser?.providerData?.map((p) => p?.providerId),
      });
      const wrapped = linkErr as { code?: string; step?: string };
      wrapped.step = 'auth_links';
      throw linkErr;
    }
  } finally {
    setSuppressAnonymousRestore(false);
  }
}

export function isAuthProviderError(err: unknown): boolean {
  const code = errCode(err);
  return code === 'auth/operation-not-allowed' || code === 'auth/admin-restricted-operation';
}

export function describeAuthError(err: unknown): string {
  const code = errCode(err);
  const step = (err as { step?: string }).step;
  if (isAuthProviderError(err)) {
    return 'Firebase 콘솔에서 Email/Password 로그인을 활성화해 주세요.';
  }
  if (code === 'permission-denied') {
    if (step === 'members_update') {
      return 'DB 권한 오류(사원 인증 연결). Firestore 규칙을 최신으로 다시 게시해 주세요.';
    }
    if (step === 'auth_links') {
      return 'DB 권한 오류(권한 링크). 규칙에 pf_auth_links 가 있는지 확인 후 다시 게시해 주세요.';
    }
    return 'DB 권한 오류입니다. Firestore 규칙을 최신으로 다시 게시해 주세요.';
  }
  if (code === 'auth/network-request-failed') {
    return '네트워크 오류입니다. 인터넷 연결을 확인해 주세요.';
  }
  if (code === 'auth/too-many-requests') {
    return '시도가 너무 많습니다. 잠시 후 다시 로그인해 주세요.';
  }
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
    return '인증 계정 비밀번호가 앱 비밀번호와 다를 수 있습니다.';
  }
  if (code) {
    return `인증 연동 실패 (${code}). 잠시 후 다시 시도해 주세요.`;
  }
  return `인증 연동 실패: ${errMessage(err)}`;
}
